import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useFetcher } from "@remix-run/react";
import { IconCheck } from "@tabler/icons-react";

import { useTranslation } from "~/hooks/use-typed-translation";


interface SuggestionActionData {
  ok: boolean;
  message?: string;
  submissionId: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  locale: "en" | "fr";
  submissionId: string;
  searchQuery: string;
  filters: Record<string, string>;
}

export const DoctorSuggestionModal = ({
  opened,
  onClose,
  locale,
  submissionId,
  searchQuery,
  filters,
}: Props) => {
  const { t } = useTranslation();
  const fetcher = useFetcher<SuggestionActionData>();
  const submitting = fetcher.state !== "idle";
  const complete =
    fetcher.data?.ok === true && fetcher.data.submissionId === submissionId;
  const error =
    fetcher.data?.ok === false && fetcher.data.submissionId === submissionId
      ? fetcher.data.message
      : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={complete ? undefined : t("doctor-suggestion-title")}
      size="lg"
      radius="lg"
      centered>
      {complete ? (
        <Stack align="center" gap="md" py={{ base: "md", sm: "xl" }}>
          <ThemeIcon color="green" variant="light" radius="xl" size={72}>
            <IconCheck size={38} stroke={2.5} />
          </ThemeIcon>
          <Title order={2} ta="center">
            {t("doctor-suggestion-success-title")}
          </Title>
          <Text ta="center" c="dimmed" maw={460}>
            {t("doctor-suggestion-success-message")}
          </Text>
          <Button color="primary" radius="xl" onClick={onClose}>
            {t("doctor-suggestion-done")}
          </Button>
        </Stack>
      ) : (
        <fetcher.Form method="post" action="/api/doctor-suggestions">
          <Stack>
            <Text size="sm" c="dimmed">
              {t("doctor-suggestion-introduction")}
            </Text>
            {searchQuery && (
              <Alert color="primary" variant="light" radius="md">
                {t("doctor-suggestion-search-context", { query: searchQuery })}
              </Alert>
            )}
            {error && <Alert color="red">{error}</Alert>}
            <input type="hidden" name="submissionId" value={submissionId} />
            <input type="hidden" name="searchQuery" value={searchQuery} />
            <input type="hidden" name="filters" value={JSON.stringify(filters)} />
            <input type="hidden" name="locale" value={locale} />
            <input type="hidden" name="sourcePage" value="doctor_search" />
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: "-10000px" }}>
              <label htmlFor="doctor-suggestion-website">Website</label>
              <input id="doctor-suggestion-website" name="website" tabIndex={-1} />
            </div>

            <TextInput
              name="doctorName"
              label={t("doctor-suggestion-doctor-name")}
              placeholder={t("doctor-suggestion-doctor-name-placeholder")}
              required
            />
            <TextInput
              name="specialty"
              label={t("doctor-suggestion-specialty")}
              placeholder={t("doctor-suggestion-specialty-placeholder")}
            />
            <Text size="xs" c="dimmed" mt={4}>
              {t("doctor-suggestion-contact-help")}
            </Text>
            <TextInput
              name="phoneNumber"
              label={t("doctor-suggestion-phone")}
              placeholder="+225 00 00 00 00 00"
              type="tel"
            />
            <TextInput
              name="email"
              label={t("doctor-suggestion-email")}
              type="email"
            />
            <TextInput
              name="socialHandle"
              label={t("doctor-suggestion-social")}
              placeholder="@doctor or profile link"
            />
            <Select
              name="relationship"
              label={t("doctor-suggestion-relationship")}
              data={[
                { value: "", label: t("doctor-suggestion-relationship-select") },
                { value: "patient", label: t("doctor-suggestion-relationship-patient") },
                { value: "colleague", label: t("doctor-suggestion-relationship-colleague") },
                { value: "friend_or_family", label: t("doctor-suggestion-relationship-friend") },
                { value: "other", label: t("doctor-suggestion-relationship-other") },
              ]}
            />
            <TextInput
              name="submitterName"
              label={t("doctor-suggestion-your-name")}
            />
            <TextInput
              name="submitterContact"
              label={t("doctor-suggestion-your-contact")}
            />
            <Text size="xs" c="dimmed">
              {t("doctor-suggestion-privacy")}
            </Text>
            <Group justify="flex-end" mt="sm">
              <Button
                variant="default"
                radius="xl"
                onClick={onClose}
                disabled={submitting}>
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                radius="xl"
                loading={submitting}>
                {t("doctor-suggestion-submit")}
              </Button>
            </Group>
          </Stack>
        </fetcher.Form>
      )}
    </Modal>
  );
};
